// Analytics Service for comprehensive tracking
import sanityAnalyticsService from './sanityAnalyticsService';

class AnalyticsService {
    constructor() {
        this.isInitialized = false;
        this.consentGiven = true;
        this.sessionId = this.generateSessionId();
        this.startTime = Date.now();
        this.userInteractions = [];
        this.apiEndpoint = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        this.currentPage = window.location.pathname;
        this.pageStartTime = Date.now();
    }

    // Generate unique session ID
    generateSessionId() {
        return 'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
    }

    // Initialize analytics with consent
    initialize(consent = false) {
        this.consentGiven = consent;
        
        if (!consent) {
            console.log('📊 Analytics disabled - no consent given');
            return;
        }

        // Initialize Google Analytics
        this.initializeGoogleAnalytics();
        
        // Track page load
        this.trackPageView(window.location.pathname);
        
        // Track session start
        this.trackEvent('session', 'start', {
            sessionId: this.sessionId,
            timestamp: new Date().toISOString()
        });

        this.isInitialized = true;
        console.log('📊 Analytics initialized with consent');
    }

    // Initialize Google Analytics
    initializeGoogleAnalytics() {
        if (typeof window !== 'undefined' && window.gtag) {
            // Google Analytics is already loaded
            console.log('📊 Google Analytics already loaded');
            return;
        }

        // Load Google Analytics script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GA_TRACKING_ID}`;
        document.head.appendChild(script);

        window.dataLayer = window.dataLayer || [];
        window.gtag = function() {
            window.dataLayer.push(arguments);
        };

        window.gtag('js', new Date());
        window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID, {
            page_title: document.title,
            page_location: window.location.href
        });

        console.log('📊 Google Analytics loaded');
    }

    // Track page views with detailed information
    trackPageView(page) {
        // Calculate time spent on previous page
        const timeSpent = Date.now() - this.pageStartTime;
        if (this.currentPage !== page && this.currentPage !== '/') {
            this.trackTimeSpent(this.currentPage, timeSpent);
        }

        // Update current page
        this.currentPage = page;
        this.pageStartTime = Date.now();

        // Google Analytics
        if (window.gtag) {
            window.gtag('config', process.env.REACT_APP_GA_TRACKING_ID, {
                page_path: page,
                page_title: document.title
            });
        }

        // Custom tracking with detailed info
        this.trackEvent('page_view', page, {
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            pageTitle: document.title,
            timeSpent: timeSpent
        });
    }

    // Track custom events with enhanced details
    trackEvent(category, action, label = null, value = null) {
        const eventData = {
            category,
            action,
            label,
            value,
            sessionId: this.sessionId,
            timestamp: new Date().toISOString(),
            url: window.location.href,
            userAgent: navigator.userAgent,
            currentPage: this.currentPage,
            timeOnPage: Date.now() - this.pageStartTime
        };

        // Google Analytics
        if (window.gtag) {
            window.gtag('event', action, {
                event_category: category,
                event_label: label,
                value: value
            });
        }

        // Store for detailed logging
        this.userInteractions.push(eventData);

        // Send to backend
        this.sendToBackend('event', eventData);
    }

    // Track form interactions with field details
    trackFormInteraction(formName, fieldName, action, value = null) {
        this.trackEvent('form_interaction', action, `${formName}.${fieldName}`, value);
    }

    // Track button clicks with context
    trackButtonClick(buttonName, location = null, context = null) {
        this.trackEvent('button_click', buttonName, location, context);
    }

    // Track voice assistant interactions with command details
    trackVoiceInteraction(command, response, duration = null) {
        const commandType = this.categorizeVoiceCommand(command);
        this.trackEvent('voice_interaction', commandType, command, {
            response: response,
            duration: duration,
            commandLength: command.length,
            isRecognized: response !== 'unrecognized_command'
        });
    }

    // Categorize voice commands for better analytics
    categorizeVoiceCommand(command) {
        const lowerCommand = command.toLowerCase();
        
        if (lowerCommand.includes('go to') || lowerCommand.includes('navigate')) {
            return 'navigation_command';
        } else if (lowerCommand.includes('chat') || lowerCommand.includes('ai') || lowerCommand.includes('ask')) {
            return 'ai_chat_command';
        } else if (lowerCommand.includes('help')) {
            return 'help_command';
        } else if (lowerCommand.includes('github') || lowerCommand.includes('linkedin') || lowerCommand.includes('resume')) {
            return 'portfolio_command';
        } else {
            return 'general_speech';
        }
    }

    // Track AI chat interactions with message details
    trackAIChatInteraction(message, response, duration = null) {
        const messageType = this.categorizeAIMessage(message);
        this.trackEvent('ai_chat', messageType, message, {
            responseLength: response ? response.length : 0,
            duration: duration,
            messageLength: message.length,
            hasResponse: !!response
        });
    }

    // Categorize AI messages for better analytics
    categorizeAIMessage(message) {
        const lowerMessage = message.toLowerCase();
        
        if (lowerMessage.includes('skill') || lowerMessage.includes('technology')) {
            return 'skills_inquiry';
        } else if (lowerMessage.includes('project') || lowerMessage.includes('work')) {
            return 'project_inquiry';
        } else if (lowerMessage.includes('experience') || lowerMessage.includes('job')) {
            return 'experience_inquiry';
        } else if (lowerMessage.includes('contact') || lowerMessage.includes('email')) {
            return 'contact_inquiry';
        } else if (lowerMessage.includes('education') || lowerMessage.includes('degree')) {
            return 'education_inquiry';
        } else {
            return 'general_inquiry';
        }
    }

    // Track time spent on page
    trackTimeSpent(page, duration) {
        this.trackEvent('time_spent', page, null, duration);
    }

    // Track navigation events
    trackNavigation(fromSection, toSection) {
        this.trackEvent('navigation', 'section_change', `${fromSection} -> ${toSection}`, {
            fromSection: fromSection,
            toSection: toSection,
            navigationMethod: 'scroll_or_click'
        });
    }

    // Track scroll behavior
    trackScroll(depth, section) {
        this.trackEvent('scroll', 'page_scroll', section, {
            scrollDepth: depth,
            section: section
        });
    }

    // Track user engagement
    trackEngagement(action, details = {}) {
        this.trackEvent('engagement', action, details.context, {
            ...details,
            timeOnSite: Date.now() - this.startTime
        });
    }

    // Send data to backend
    async sendToBackend(type, data) {
        try {
            // Send to Express backend
            const response = await fetch(`${this.apiEndpoint}/api/analytics/${type}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    ...data,
                    type,
                    sessionId: this.sessionId,
                    timestamp: new Date().toISOString()
                })
            });

            if (!response.ok) {
                console.warn('📊 Analytics backend error:', response.status);
            }

            // Also send to Sanity CMS
            try {
                await sanityAnalyticsService.createAnalyticsEvent({
                    ...data,
                    sessionId: this.sessionId,
                    timestamp: new Date().toISOString(),
                    consentGiven: this.consentGiven,
                    sessionStartTime: new Date(this.startTime).toISOString(),
                    sessionDuration: Math.round((Date.now() - this.startTime) / 1000)
                });
                console.log('📊 Analytics event saved to Sanity CMS');
            } catch (sanityError) {
                console.warn('📊 Sanity CMS error:', sanityError);
            }

        } catch (error) {
            console.warn('📊 Analytics backend error:', error);
        }
    }

    // Get user location and IP
    async getLocationData() {
        try {
            const response = await fetch(`${this.apiEndpoint}/api/analytics/location`);
            if (response.ok) {
                const locationData = await response.json();
                this.trackEvent('location', 'detected', `${locationData.city}, ${locationData.country}`, {
                    ip: locationData.ip,
                    country: locationData.country,
                    city: locationData.city,
                    timezone: locationData.timezone
                });
                return locationData;
            }
        } catch (error) {
            console.warn('📊 Location tracking error:', error);
        }
        return null;
    }

    // Update consent
    updateConsent(consent) {
        this.consentGiven = consent;
        
        if (consent && !this.isInitialized) {
            this.initialize(true);
        }
        
        this.trackEvent('consent', 'updated', consent ? 'granted' : 'denied');
    }

    // Get analytics summary
    getAnalyticsSummary() {
        return {
            sessionId: this.sessionId,
            startTime: this.startTime,
            duration: Date.now() - this.startTime,
            interactions: this.userInteractions.length,
            consentGiven: this.consentGiven,
            currentPage: this.currentPage,
            timeOnCurrentPage: Date.now() - this.pageStartTime
        };
    }

    // Clean up on page unload
    cleanup() {
        if (this.consentGiven) {
            const duration = Date.now() - this.startTime;
            this.trackTimeSpent(window.location.pathname, duration);
            this.trackEvent('session', 'end', null, duration);
        }
    }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Initialize on page load if consent was previously given
if (typeof window !== 'undefined') {
    const savedConsent = localStorage.getItem('analytics_consent');
    if (savedConsent === 'true') {
        analyticsService.initialize(true);
    }

    // Cleanup on page unload
    window.addEventListener('beforeunload', () => {
        analyticsService.cleanup();
    });
}

export default analyticsService; 